import { Maximize2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useTrendCompare, renderCompareAreas, CompareControls, TrendComparePanel, signalFromUnit } from './TrendCompare';
import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Copy, Check } from 'lucide-react';
import { apiFetch } from '../api/client';
import { NoData } from './DataState';
import { WaveformViewer, WaveformJumpTo } from './WaveformViewer';
import { WfState, resolveWfState, isPlottable, wfMarkerColour, wfMarkerDash, fmtWfLabel } from '../lib/wfState';
import { HierarchyBreadcrumb, Hierarchy } from './HierarchyBreadcrumb';

interface PointData {
  V: number;
  H: number;
  A: number;
}

interface Thresholds {
  yellow: number;
  red: number;
}

interface Point {
  id: string;
  name: string;
  velocity: PointData;
  acceleration: PointData;
  envelope: PointData;
  temperature: PointData;
  velocityThresholds?: Thresholds;
  accelerationThresholds?: Thresholds;
  envelopeThresholds?: Thresholds;
  temperatureThresholds?: Thresholds;
  velocityThresholdsByAxis?: Record<string, Thresholds>;
  accelerationThresholdsByAxis?: Record<string, Thresholds>;
  envelopeThresholdsByAxis?: Record<string, Thresholds>;
  status: 'low' | 'medium' | 'high';
  machineId?: string;
  axisIds?: number[];
  axisIdsByLabel?: Record<string, number>;
  velHasOverride?: boolean;
  velOverrideSetAt?: string;
  velOverrideSetBy?: string;
  accelHasOverride?: boolean;
  accelOverrideSetAt?: string;
  accelOverrideSetBy?: string;
  envHasOverride?: boolean;
  envOverrideSetAt?: string;
  envOverrideSetBy?: string;
  // Per-axis override metadata (V/H/A). The point-level scalars above are a
  // first-axis-wins rollup and cannot say whether THIS axis is overridden.
  velOverrideByAxis?: Record<string, OverrideMeta>;
  accelOverrideByAxis?: Record<string, OverrideMeta>;
  envOverrideByAxis?: Record<string, OverrideMeta>;
}

type OverrideMeta = { active: boolean; setAt?: string; setBy?: string };

interface PointDetailProps {
  point: Point;
  onBack: () => void;
  onThresholdsUpdate?: (pointId: string, metric: string, thresholds: Thresholds) => void;
  // Fired only after a threshold override is PERSISTED (save or clear) — never on a
  // slider drag. The parent uses it to refetch /machines/{id} so its machineAxes
  // snapshot (which seeds this point's threshold props) is reconciled to server truth.
  onSaved?: () => void;
  initialAxisNum?: number;
  // Ancestors for the sticky breadcrumb. Supplied by MachineDetail, which is the
  // only render site — it already holds both from /machines/{id}.
  hierarchy?: Hierarchy | null;
  machineName?: string;
}

const AXIS_COLORS: Record<string, string> = { V: '#8884d8', H: '#82ca9d', A: '#FF0000' };

// F-25: the Math.sin synthetic-trend generator was removed. Empty/errored
// trend data now renders an honest "no data" state (see chart empty-guards
// and NoData below) — never fabricated data.


function applyFloor(data: any[], floor: number): any[] {
  if (floor <= 0) return data;
  return data.map(entry => {
    const out: any = { time: entry.time };
    for (const [k, v] of Object.entries(entry)) {
      if (k === 'time') continue;
      out[k] = typeof v === 'number' && v < floor ? null : v;
    }
    return out;
  });
}

function FloorSlider({ floor, setFloor, yMax }: { floor: number; setFloor: (v: number) => void; yMax: number }) {
  const step = yMax > 0 ? yMax / 200 : 0.001;
  return (
    <div className="flex flex-col items-center justify-between shrink-0" style={{ height: 300, width: 28, paddingTop: 6, paddingBottom: 4 }}>
      <span className="font-mono tabular-nums text-center" style={{ fontSize: 9, color: '#64748b', minHeight: 12, lineHeight: '12px', writingMode: 'horizontal-tb' }}>
        {floor > 0 ? floor.toFixed(3) : ''}
      </span>
      <input
        type="range" min={0} max={yMax || 1} step={step}
        value={Math.min(floor, yMax || 1)}
        onChange={e => setFloor(Number(e.target.value))}
        style={{ writingMode: 'vertical-lr', direction: 'rtl', height: 240, width: 18, cursor: 'pointer', accentColor: '#64748b', flexShrink: 0 }}
        title="Drag to filter: hides data below this value"
      />
      <span style={{ fontSize: 9, color: '#9ca3af' }}>0</span>
    </div>
  );
}

// Slider granularity: ~180 steps across the whole track, snapped to a 1/2/5×10^n
// increment. The thresholds previously stepped at 0.0001 G over a range of ~2 G —
// thousands of steps across ~300 px, so a single pixel of travel moved the value
// several times and the control could not be landed on a round number. One step
// per pixel is the point; the number input beside it is for exact values.
function niceStep(range: number): number {
  if (!(range > 0) || !isFinite(range)) return 0.001;
  const raw = range / 180;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  return (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
}

// Editable threshold value. Kept as a draft string while focused so a partial
// entry ("0.", "1.2e") is not parsed and written back mid-keystroke; commits on
// blur or Enter, reverts on Escape. Arrow keys nudge by one slider step.
function ThresholdInput({ value, decimals, step, unitLabel, onCommit, label, disabled }: {
  value: number;
  decimals: number;
  step: number;
  unitLabel: string;
  onCommit: (v: number) => void;
  label: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const n = parseFloat(draft);
    setDraft(null);
    if (isFinite(n) && n >= 0) onCommit(parseFloat(n.toFixed(6)));
  };
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step={step}
        disabled={disabled}
        aria-label={label}
        value={draft ?? value.toFixed(decimals)}
        onChange={e => setDraft(e.target.value)}
        onFocus={e => e.currentTarget.select()}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur(); }
        }}
        className="w-24 px-2 py-1 text-sm text-right tabular-nums border border-gray-300 rounded-md
                   focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500
                   disabled:bg-gray-50 disabled:text-gray-400"
      />
      <span className="text-xs text-gray-400 w-9 shrink-0">{unitLabel}</span>
    </div>
  );
}

function calcYMax(data: any[], threshMod?: number | null): number {
  const vals = data.flatMap(d =>
    Object.entries(d)
      .filter(([k]) => k !== 'time')
      .map(([, v]) => v as number)
      .filter(v => v != null && !isNaN(v))
  );
  const dataMax = vals.length > 0 ? Math.max(...vals) : 0;
  const top = Math.max(dataMax, threshMod ?? 0);
  return top > 0 ? parseFloat((top * 1.2).toFixed(4)) : 1;
}

// Build Map<bucket, {file_id, measured_at, wf_state}> for waveform dots from raw
// trend rows. Serveability comes from ../lib/wfState — the same helper the machine
// page uses, so the two pages cannot disagree about the same capture (they did:
// this builder tested the retired `wf_serveable` and so rendered every point as
// fast, promising <100ms on captures that take ~2.4s).
function buildWfMarkers(rows: any[], pointName: string): Map<string, { file_id: number; measured_at: string; wf_state: WfState }> {
  const result = new Map<string, { file_id: number; measured_at: string; wf_state: WfState }>();
  for (const r of rows) {
    const wfState = resolveWfState(r);
    if (!r.file_id || r.reason !== 2 || !isPlottable(wfState) || r.point_name !== pointName) continue;
    const bucket = (r.measured_at || '').substring(0, 10).replace(/\//g, '-');
    if (!result.has(bucket)) result.set(bucket, { file_id: r.file_id, measured_at: r.measured_at, wf_state: wfState });
  }
  return result;
}

// Find nearest time bucket that has a waveform, searching outward from activeIdx.
function findNearestWfPt(
  data: any[],
  idx: number,
  markersMap: Map<string, { file_id: number; measured_at: string; wf_state: WfState }> | undefined,
  maxSearch = 10
): { time: string; measured_at: string; wf_state: WfState } | null {
  if (!markersMap) return null;
  for (let d = 0; d <= maxSearch; d++) {
    for (const off of d === 0 ? [0] : [d, -d]) {
      const i = idx + off;
      if (i < 0 || i >= data.length) continue;
      const time = data[i]?.time;
      const hit = time ? markersMap.get(time) : undefined;
      if (hit) return { time, measured_at: hit.measured_at, wf_state: hit.wf_state };
    }
  }
  return null;
}

// Chart Container with Copy functionality
function ChartContainer({ children, title, onExpand }: { children: React.ReactNode; title: string; onExpand?: () => void }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyChart = async () => {
    if (!chartRef.current) return;
    try {
      const svgElement = chartRef.current.querySelector('svg');
      if (!svgElement) return;
      const rect = svgElement.getBoundingClientRect();
      const canvas = document.createElement('canvas');
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(2, 2);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, rect.width, rect.height);
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = async () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {}
        });
      };
      img.src = url;
    } catch {}
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          {onExpand && (
            <button onClick={onExpand} className="p-2 hover:bg-gray-100 rounded-lg transition-all group" title="Expand chart">
              <Maximize2 className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
            </button>
          )}
          <button onClick={handleCopyChart} className="p-2 hover:bg-gray-100 rounded-lg transition-all group" title="Copy chart to clipboard">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />}
          </button>
        </div>
      </div>
      <div ref={chartRef}>{children}</div>
    </div>
  );
}

const PV_PLUS_FAULT_COLOURS: Record<string, string> = { highly_periodic: '#dc2626', bearing_fault: '#dc2626', cavitation_noise: '#6b7280', lubrication: '#d97706', early_fault: '#0891b2', healthy: '#16a34a' };
const PV_PLUS_FAULT_LABELS: Record<string, string>  = { highly_periodic: 'Highly Periodic', bearing_fault: 'Highly Periodic', cavitation_noise: 'Cavitation/noise', lubrication: 'Lubrication', early_fault: 'Early fault', healthy: 'Healthy' };

export function PointDetail({ point, onBack, onThresholdsUpdate, onSaved, initialAxisNum, hierarchy, machineName }: PointDetailProps) {
  // Shared time range for all charts (days)
  const [trendDays, setTrendDays] = useState(90);
  const cmp = useTrendCompare();
  // Active tab: 2=Velocity, 0=Acceleration, 6=Envelope, -1=PeakVue
  const [primaryUnit, setPrimaryUnit] = useState<number>(2);
  // PeakVue per-axis data
  const [pvAxisData, setPvAxisData] = useState<Record<string, { time: string; value: number; ft?: string; pp?: number; rel?: string; at?: string }[]>>({});
  const [pvAxisThresholds, setPvAxisThresholds] = useState<Record<string, { moderate: number; severe: number; is_fallback: boolean } | null>>({});
  const [pvLoading, setPvLoading] = useState(false);
  const [pvHiddenAxes, setPvHiddenAxes] = useState<Set<string>>(new Set());
  const togglePvAxis = (lbl: string) => setPvHiddenAxes(prev => { const s = new Set(prev); if (s.has(lbl)) s.delete(lbl); else s.add(lbl); return s; });
  const pvHoverRef = useRef<{ t: string; axis: string } | null>(null);

  const [velocityThresholds, setVelocityThresholds] = useState(point.velocityThresholds ?? { yellow: 0, red: 0 });
  const [accelerationThresholds, setAccelerationThresholds] = useState(point.accelerationThresholds ?? { yellow: 0, red: 0 });
  const [envelopeThresholds, setEnvelopeThresholds] = useState(point.envelopeThresholds ?? { yellow: 0, red: 0 });
  const [temperatureThresholds, setTemperatureThresholds] = useState(point.temperatureThresholds ?? { yellow: 0, red: 0 });
  // Per-axis thresholds for dynamic chart reference lines (read-only, driven by prop)
  const velThrByAxis = point.velocityThresholdsByAxis ?? {};
  const accelThrByAxis = point.accelerationThresholdsByAxis ?? {};
  const envThrByAxis = point.envelopeThresholdsByAxis ?? {};

  const [velocityDirty, setVelocityDirty] = useState(false);
  const [accelerationDirty, setAccelerationDirty] = useState(false);
  const [envelopeDirty, setEnvelopeDirty] = useState(false);
  const [savingMetric, setSavingMetric] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMetric, setSavedMetric] = useState<string | null>(null);

  // Override metadata, per metric PER AXIS. Read from the parent snapshot and
  // overlaid with what the server returned for the one axis this page just wrote —
  // the parent's onSaved refetch lands a moment later and is authoritative for
  // every axis we did not touch.
  const [ovOverlay, setOvOverlay] = useState<Record<string, Record<string, OverrideMeta>>>({});
  const setOverrideMeta = (metric: string, axis: string, meta: OverrideMeta) =>
    setOvOverlay(prev => ({ ...prev, [metric]: { ...(prev[metric] ?? {}), [axis]: meta } }));
  const overrideFor = (metric: string, axis: string | null): OverrideMeta => {
    if (!axis) return { active: false };
    const fromProp =
      metric === 'velocity'     ? point.velOverrideByAxis?.[axis] :
      metric === 'acceleration' ? point.accelOverrideByAxis?.[axis] :
                                  point.envOverrideByAxis?.[axis];
    return ovOverlay[metric]?.[axis] ?? fromProp ?? { active: false };
  };

  // PeakVue threshold slider state
  const [pvSlideMod, setPvSlideMod] = useState(5.0);
  const [pvSlideSev, setPvSlideSev] = useState(10.0);
  const [pvDirty, setPvDirty] = useState(false);
  const [pvSaving, setPvSaving] = useState(false);
  const [pvSaveError, setPvSaveError] = useState<string | null>(null);

  const [velFloor,   setVelFloor]   = useState(0);
  const [accelFloor, setAccelFloor] = useState(0);
  const [envFloor,   setEnvFloor]   = useState(0);
  const [tempFloor,  setTempFloor]  = useState(0);

  type AxisRow = { time: string; V: number | null; H: number | null; A: number | null };
  const [trendData, setTrendData] = useState<Record<number, AxisRow[]>>({ 0: [], 2: [], 6: [] });

  // General-purpose channels (Perfect Pump step 7). ONE SERIES PER CHANNEL, never
  // combined: a GP sensor's channels are independent variables in different
  // engineering units — Suction PSI and Oil Temperature cannot share a Y axis the
  // way H/V/A can, because H/V/A are the same quantity measured three ways.
  type GpSeries = {
    axisId: number; label: string; unit: string; unitCode: number;
    rows: { time: string; value: number | null }[];
  };
  const [gpSeries, setGpSeries] = useState<GpSeries[]>([]);
  const [sensorTempData, setSensorTempData] = useState<{ time: string; temp: number }[]>([]);

  // Waveform state
  const [wfMarkersMap, setWfMarkersMap] = useState<Record<number, Map<string, { file_id: number; measured_at: string; wf_state: WfState }>>>({});
  const [waveformJumpTo, setWaveformJumpTo] = useState<WaveformJumpTo | null>(null);
  // Second waveform viewer for point-vs-point comparison. Capped at one extra.
  const [compareViewer, setCompareViewer] = useState(false);
  const [wfHover, setWfHover] = useState<{ unitCode: number; time: string; measured_at: string; wf_state: WfState } | null>(null);
  const wfHoverRef = useRef<{ unitCode: number; time: string; measured_at: string; wf_state: WfState } | null>(null);
  wfHoverRef.current = wfHover;

  const MM_TO_IN = 1 / 25.4;

  const buildAxisRows = (rows: any[], unitCode: number): AxisRow[] => {
    const scale = unitCode === 2 ? MM_TO_IN : 1;
    const buckets = new Map<string, { V: number[]; H: number[]; A: number[] }>();
    for (const r of rows.filter(r => r.point_name === point.name)) {
      if (r.value == null) continue;
      const day = (r.measured_at || '').substring(0, 10).replace(/\//g, '-');
      if (!buckets.has(day)) buckets.set(day, { V: [], H: [], A: [] });
      const b = buckets.get(day)!;
      const v = r.value * scale;
      if      (r.axis_num === 2) b.V.push(v);
      else if (r.axis_num === 1) b.H.push(v);
      else if (r.axis_num === 3) b.A.push(v);
    }
    const decimals = unitCode === 2 ? 4 : 3;
    const peak = (arr: number[]): number | null =>
      arr.length ? parseFloat(Math.max(...arr).toFixed(decimals)) : null;
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, b]) => ({ time: day, V: peak(b.V), H: peak(b.H), A: peak(b.A) }));
  };

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=2&days=${trendDays}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 2: buildAxisRows(rows, 2) }));
        setWfMarkersMap(prev => ({ ...prev, 2: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, trendDays]);

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=0&days=${trendDays}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 0: buildAxisRows(rows, 0) }));
        setWfMarkersMap(prev => ({ ...prev, 0: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, trendDays]);

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=6&days=${trendDays}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 6: buildAxisRows(rows, 6) }));
        setWfMarkersMap(prev => ({ ...prev, 6: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, trendDays]);

  // GP channels: 9061 = analog in ENGINEERING units (derived from the 9060 raw by
  // the */15 net), 9062 = current clamp, already Amps. The raw 9060 volts are
  // deliberately NOT charted — that is the instrument reading, not the measurement
  // the operator cares about.
  //
  // The label and unit come from the SERVER (axis_label / eng_unit, resolved from
  // gp_channel_config), never from axis_num: a GP axis_num is just an allocation
  // order, so anything positional would render "11" where it should say
  // "Suction PSI". Vibration points fetch these too and simply get [] back.
  useEffect(() => {
    if (!point.machineId) return;
    let cancelled = false;
    const days = trendDays;
    Promise.all([9061, 9062].map(uc =>
      apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=${uc}&days=${days}`)
        .then(rows => (rows ?? []).map(r => ({ ...r, _uc: uc })))
        .catch(() => [] as any[])
    )).then(groups => {
      if (cancelled) return;
      const mine = groups.flat().filter(r =>
        r.point_name === point.name && r.value != null && r.axis_id != null);

      // Daily MEAN, matching the Temperature card this clones — and correct for a
      // process value. The vibration charts take the daily MAX because a peak is
      // the diagnostic event; a pump's discharge pressure is a level, where the
      // max would report the worst transient of the day as if it were the reading.
      const byAxis = new Map<number, GpSeries & { buckets: Map<string, number[]> }>();
      for (const r of mine) {
        let s = byAxis.get(r.axis_id);
        if (!s) {
          s = {
            axisId: r.axis_id,
            label: r.axis_label || `Axis ${r.axis_num}`,
            unit: r.eng_unit || '',
            unitCode: r._uc,
            rows: [],
            buckets: new Map(),
          };
          byAxis.set(r.axis_id, s);
        }
        const day = (r.measured_at || '').substring(0, 10).replace(/\//g, '-');
        if (!s.buckets.has(day)) s.buckets.set(day, []);
        s.buckets.get(day)!.push(r.value);
      }

      const series: GpSeries[] = Array.from(byAxis.values()).map(s => ({
        axisId: s.axisId, label: s.label, unit: s.unit, unitCode: s.unitCode,
        rows: Array.from(s.buckets.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([day, vals]) => ({
            time: day,
            value: parseFloat((vals.reduce((t, v) => t + v, 0) / vals.length).toFixed(2)),
          })),
      })).sort((a, b) => a.axisId - b.axisId);   // allocation order == channel order
      setGpSeries(series);
    });
    return () => { cancelled = true; };
  }, [point.machineId, point.name, trendDays]);

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/sensor-health?days=${trendDays}`)
      .then(rows => {
        const mine = (rows ?? []).filter(r => r.point_name === point.name && r.sensor_temperature != null);
        const buckets = new Map<string, number[]>();
        for (const r of mine) {
          const day = (r.polled_at || '').substring(0, 10);
          if (!buckets.has(day)) buckets.set(day, []);
          buckets.get(day)!.push(r.sensor_temperature);
        }
        const result = Array.from(buckets.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([day, vals]) => ({
            time: day,
            temp: parseFloat((vals.reduce((s, v) => s + v, 0) / vals.length * 9 / 5 + 32).toFixed(1)),
          }));
        setSensorTempData(result);
      })
      .catch(() => setSensorTempData([]));
  }, [point.machineId, point.name, trendDays]);

  // PeakVue per-axis fetch
  useEffect(() => {
    const axisIdsByLabel = point.axisIdsByLabel;
    if (!point.machineId || !axisIdsByLabel || Object.keys(axisIdsByLabel).length === 0) return;
    setPvHiddenAxes(new Set());
    setPvLoading(true);
    const fetchAxis = async (label: string, axisId: number) => {
      const data = await apiFetch<any>(`/machines/${point.machineId}/peakvue-trend?days=${trendDays}&axis_id=${axisId}`);
      // Axis-level response: { level: 'axis', readings: [...], alarm_thresholds: {...} }
      const readings: any[] = data?.level === 'axis'
        ? (data?.readings ?? [])
        : (data?.points ?? []).flatMap((pt: any) => pt.readings ?? []);
      const thr = data?.alarm_thresholds
        ? { moderate: data.alarm_thresholds.moderate, severe: data.alarm_thresholds.severe, is_fallback: !!data.alarm_thresholds.is_fallback }
        : null;
      const weekly = trendDays > 30;
      const bucketMap = new Map<string, any>();
      for (const r of readings) {
        const dt: string = r.measured_at || '';
        let bucket: string;
        if (weekly) {
          const d = new Date(dt.substring(0, 10).replace(/\//g, '-'));
          const day = d.getDay();
          d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
          bucket = d.toISOString().substring(0, 10);
        } else { bucket = dt.substring(0, 10).replace(/\//g, '-'); }
        const prev = bucketMap.get(bucket);
        if (!prev || r.max_peak_g > prev.value) {
          bucketMap.set(bucket, {
            time: bucket,
            value: r.max_peak_g,
            at: r.measured_at,
            ...(r.max_peak_g >= 5 && r.fault_type ? { ft: r.fault_type, pp: r.periodic_pct, rel: r.plus_reliability } : {}),
          });
        }
      }
      return { rows: Array.from(bucketMap.values()).sort((a, b) => a.time.localeCompare(b.time)), thr };
    };
    Promise.all(
      Object.entries(axisIdsByLabel).map(([label, id]) => fetchAxis(label, id).then(({ rows, thr }) => [label, rows, thr] as const))
    ).then(results => {
      const outData: Record<string, any[]> = {};
      const outThr: Record<string, { moderate: number; severe: number; is_fallback: boolean } | null> = {};
      for (const [label, rows, thr] of results) { outData[label] = rows; outThr[label] = thr; }
      setPvAxisData(outData);
      setPvAxisThresholds(outThr);
    }).catch(() => {}).finally(() => setPvLoading(false));
  }, [point.machineId, point.axisIdsByLabel, trendDays]);

  // Memoized raw (real data or fallback) and filtered arrays
  // F-25: real trend data only — empty stays empty (chart shows NoData), never synthetic.
  const rawVel   = useMemo(() => trendData[2], [trendData[2]]);
  const rawAccel = useMemo(() => trendData[0], [trendData[0]]);
  const rawEnv   = useMemo(() => trendData[6], [trendData[6]]);

  // F-23: Current Values are derived from the latest REAL trend bucket, not the
  // TreeView {V:0,H:0,A:0} stubs. Absence renders "—", never a fabricated 0.00.
  const latestBucket = (uc: number): AxisRow | null => {
    const arr = trendData[uc];
    return arr && arr.length ? arr[arr.length - 1] : null;
  };
  const curVel = latestBucket(2), curAccel = latestBucket(0), curEnv = latestBucket(6);
  const curTemp = sensorTempData.length ? sensorTempData[sensorTempData.length - 1].temp : null;
  const fmtCur = (v: number | null | undefined, digits: number): string =>
    (v == null || Number.isNaN(v)) ? '—' : v.toFixed(digits);
  const filteredVel   = useMemo(() => applyFloor(rawVel,         velFloor),   [rawVel,         velFloor]);
  const filteredAccel = useMemo(() => applyFloor(rawAccel,     accelFloor),   [rawAccel,     accelFloor]);
  const filteredEnv   = useMemo(() => applyFloor(rawEnv,         envFloor),   [rawEnv,         envFloor]);
  const filteredTemp  = useMemo(() => applyFloor(sensorTempData, tempFloor),  [sensorTempData, tempFloor]);

  const UNIT_CODE: Record<string, number> = { velocity: 2, acceleration: 0, envelope: 6, temperature: 20 };

  const handleThresholdChange = (metric: string, level: 'yellow' | 'red', value: number) => {
    // When moderate (yellow) changes, auto-set severe (red) = moderate × 1.5
    const applyLevel = (base: Thresholds): Thresholds => {
      if (level === 'yellow') return { yellow: value, red: parseFloat((value * 1.5).toFixed(6)) };
      return { ...base, red: value };
    };
    if (metric === 'velocity') {
      const updated = applyLevel(velocityThresholds);
      setVelocityThresholds(updated);
      setVelocityDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'velocity', updated);
    } else if (metric === 'acceleration') {
      const updated = applyLevel(accelerationThresholds);
      setAccelerationThresholds(updated);
      setAccelerationDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'acceleration', updated);
    } else if (metric === 'envelope') {
      const updated = applyLevel(envelopeThresholds);
      setEnvelopeThresholds(updated);
      setEnvelopeDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'envelope', updated);
    } else if (metric === 'temperature') {
      const updated = applyLevel(temperatureThresholds);
      setTemperatureThresholds(updated);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'temperature', updated);
    }
  };

  const handleSave = async (metric: string) => {
    const unitCode = UNIT_CODE[metric];
    if (unitCode == null) return;
    // One axis, one row. Fanning this out over point.axisIds wrote the same
    // override to H, V and A, so an operator setting V silently re-thresholded
    // the other two against V's statistics.
    if (!targetAxisLabel || targetAxisId == null) {
      setSaveError('Isolate a single axis (V, H or A) before saving — thresholds are set per axis.');
      return;
    }
    const threshMap: Record<string, Thresholds> = {
      velocity: velocityThresholds,
      acceleration: accelerationThresholds,
      envelope: envelopeThresholds,
    };
    const t = threshMap[metric];
    if (!t) return;
    if (!(t.red > t.yellow)) {
      setSaveError('Critical threshold must be greater than the alarm threshold.');
      return;
    }

    setSavingMetric(metric);
    setSaveError(null);
    setSavedMetric(null);
    try {
      // PATCH the target axis only; endpoint returns effective threshold for round-trip re-seed
      const resp = await apiFetch<any>(`/alarm-manager/${targetAxisId}/${unitCode}`, {
        method: 'PATCH',
        body: JSON.stringify({ moderate_override: t.yellow, severe_override: t.red }),
      });
      // Round-trip: re-seed sliders from what the server actually persisted
      if (resp && !resp.cleared) {
        const newMod = resp.effective_moderate ?? t.yellow;
        const newSev = resp.effective_severe   ?? t.red;
        const newThresholds: Thresholds = { yellow: newMod, red: newSev };
        setOverrideMeta(metric, targetAxisLabel, { active: !!resp.has_override, setAt: resp.set_at, setBy: resp.set_by });
        if (metric === 'velocity') {
          setVelocityThresholds(newThresholds);
          setVelocityDirty(false);
        } else if (metric === 'acceleration') {
          setAccelerationThresholds(newThresholds);
          setAccelerationDirty(false);
        } else if (metric === 'envelope') {
          setEnvelopeThresholds(newThresholds);
          setEnvelopeDirty(false);
        }
        if (onThresholdsUpdate) onThresholdsUpdate(point.id, metric, newThresholds);
      }
      // Reconcile the parent's snapshot to server truth. Without this the persisted
      // value reverts on the next render from a stale prop (Back/reopen, sidebar nav,
      // or an axis-visibility toggle re-seeding from velThrByAxis).
      onSaved?.();
      setSavedMetric(metric);
      setTimeout(() => setSavedMetric(null), 3000);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Save failed — not persisted. Check you have admin access.');
    } finally {
      setSavingMetric(null);
    }
  };

  const handleClearOverride = async (metric: string) => {
    const unitCode = UNIT_CODE[metric];
    if (unitCode == null) return;
    // Clears the target axis only — the other axes keep whatever they were set to.
    if (!targetAxisLabel || targetAxisId == null) {
      setSaveError('Isolate a single axis (V, H or A) before clearing — overrides are per axis.');
      return;
    }
    setSavingMetric(metric);
    setSaveError(null);
    setSavedMetric(null);
    try {
      await apiFetch<any>(`/alarm-manager/${targetAxisId}/${unitCode}`, {
        method: 'PATCH',
        body: JSON.stringify({ moderate_override: null, severe_override: null }),
      });
      // After clear, re-read the same axis to pick up its computed baseline
      const baseline = await apiFetch<any>(`/alarm-manager/${targetAxisId}/${unitCode}`);
      const newThresholds: Thresholds = { yellow: baseline.effective_moderate ?? 0, red: baseline.effective_severe ?? 0 };
      setOverrideMeta(metric, targetAxisLabel, { active: false });
      if (metric === 'velocity') {
        setVelocityThresholds(newThresholds);
        setVelocityDirty(false);
      } else if (metric === 'acceleration') {
        setAccelerationThresholds(newThresholds);
        setAccelerationDirty(false);
      } else if (metric === 'envelope') {
        setEnvelopeThresholds(newThresholds);
        setEnvelopeDirty(false);
      }
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, metric, newThresholds);
      onSaved?.();
      setSavedMetric(metric + '_cleared');
      setTimeout(() => setSavedMetric(null), 3000);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Clear failed — not persisted.');
    } finally {
      setSavingMetric(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'low': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Render filled dot (axis color) when waveform available or at last data point, small semi-transparent dot otherwise
  const makeDot = (unitCode: number, axisColor: string, lastIdx?: number) => (p: any) => {
    const { cx, cy, payload, index } = p;
    if (!cx || !cy) return <circle key={index} r={0} />;
    const isLast = lastIdx !== undefined && index === lastIdx;
    const wf = wfMarkersMap[unitCode]?.get(payload.time);
    if (wf || isLast) return <circle key={index} cx={cx} cy={cy} r={4} fill={axisColor} stroke="white" strokeWidth={1.5} />;
    return <circle key={index} cx={cx} cy={cy} r={2} fill={axisColor} fillOpacity={0.5} />;
  };

  // V/H/A series toggle state — pre-select a single axis when coming from sidebar axis click
  const [hiddenAxes, setHiddenAxes] = useState<Set<string>>(() => {
    if (!initialAxisNum) return new Set<string>();
    const shown = initialAxisNum === 1 ? 'H' : initialAxisNum === 2 ? 'V' : 'A';
    return new Set(['H', 'V', 'A'].filter(a => a !== shown));
  });
  const toggleAxis = (axis: string) => setHiddenAxes(prev => {
    const next = new Set(prev);
    if (next.has(axis)) next.delete(axis); else next.add(axis);
    return next;
  });
  // Isolate one axis: show only it. Used by the per-axis threshold cards so an
  // operator can pick an edit target without hunting the legend toggles.
  const isolateAxis = (axis: string) => setHiddenAxes(new Set(['V', 'H', 'A'].filter(a => a !== axis)));

  // ── Edit target ────────────────────────────────────────────────────────────
  // A threshold override is a per-axis row in threshold_overrides. The edit target
  // is the axis currently isolated on the chart; with 0 or 2+ axes visible there is
  // no unambiguous target and saving is blocked (the slider then shows the max
  // across visible axes, which is a summary, not any one axis's value).
  //
  // Resolve the id through axisIdsByLabel — NEVER by indexing axisIds, which is in
  // API row order and silently targets the wrong axis on a point missing an axis.
  const visibleAxisList  = (['V', 'H', 'A'] as const).filter(a => !hiddenAxes.has(a));
  const targetAxisLabel  = visibleAxisList.length === 1 ? visibleAxisList[0] : null;
  const targetAxisId     = targetAxisLabel ? point.axisIdsByLabel?.[targetAxisLabel] : undefined;

  const pvVisibleAxisList = (['V', 'H', 'A'] as const).filter(a => !pvHiddenAxes.has(a));
  const pvTargetAxisLabel = pvVisibleAxisList.length === 1 ? pvVisibleAxisList[0] : null;
  const pvTargetAxisId    = pvTargetAxisLabel ? point.axisIdsByLabel?.[pvTargetAxisLabel] : undefined;

  // Re-seed the sliders whenever the visible axis set changes. With one axis
  // visible the sliders show THAT axis's persisted threshold — the value a save
  // will write. With 0 or 2+ visible they show the max across them, which is a
  // summary of several axes and belongs to none of them; saving is blocked in
  // that state (see targetAxisLabel).
  //
  // A pending edit is DISCARDED on an axis change, and the dirty flag cleared.
  // The old `if (velocityDirty) return;` guard preserved it instead, which meant
  // an edit typed against V could be saved onto H a click later — the same
  // wrong-axis write this page was fixed for. An edit belongs to the axis it was
  // made on; switching axes abandons it.
  useEffect(() => {
    const visible = (['V', 'H', 'A'] as const).filter(a => !hiddenAxes.has(a));
    const seed = (
      byAxis: Record<string, Thresholds>,
      set: (t: Thresholds) => void,
      setDirty: (d: boolean) => void,
    ) => {
      // Cleared unconditionally: an abandoned edit must not stay pending against
      // an axis it was not made on, even where there is nothing to re-seed from.
      setDirty(false);
      if (Object.keys(byAxis).length === 0) return;
      if (visible.length === 1 && byAxis[visible[0]]) {
        set(byAxis[visible[0]]);
        return;
      }
      const ys = visible.map(a => byAxis[a]?.yellow ?? 0).filter(v => v > 0);
      const rs = visible.map(a => byAxis[a]?.red    ?? 0).filter(v => v > 0);
      if (ys.length) set({ yellow: Math.max(...ys), red: Math.max(...rs) });
    };
    seed(velThrByAxis,   setVelocityThresholds,     setVelocityDirty);
    seed(accelThrByAxis, setAccelerationThresholds, setAccelerationDirty);
    seed(envThrByAxis,   setEnvelopeThresholds,     setEnvelopeDirty);
    setSaveError(null);
    setSavedMetric(null);
  }, [hiddenAxes]);

  // Sync PeakVue sliders when the loaded thresholds or the visible axis change.
  // Same discard-on-axis-change rule as above.
  useEffect(() => {
    if (Object.keys(pvAxisThresholds).length === 0) return;
    setPvDirty(false);
    setPvSaveError(null);
    const visible = (['V', 'H', 'A'] as const).filter(a => !pvHiddenAxes.has(a));
    if (visible.length === 1 && pvAxisThresholds[visible[0]]) {
      const t = pvAxisThresholds[visible[0]]!;
      setPvSlideMod(t.moderate);
      setPvSlideSev(t.severe);
    } else {
      const mods = visible.map(a => pvAxisThresholds[a]?.moderate ?? 0).filter(v => v > 0);
      const sevs = visible.map(a => pvAxisThresholds[a]?.severe   ?? 0).filter(v => v > 0);
      if (mods.length) { setPvSlideMod(Math.max(...mods)); setPvSlideSev(Math.max(...sevs)); }
    }
  }, [pvAxisThresholds, pvHiddenAxes]);

  const handlePvChange = (level: 'moderate' | 'severe', value: number) => {
    if (level === 'moderate') { setPvSlideMod(value); setPvSlideSev(parseFloat((value * 1.5).toFixed(4))); }
    else setPvSlideSev(value);
    setPvDirty(true);
  };

  const handlePvSave = async () => {
    // Per-axis, same as the RMS thresholds — the previous fan-out over every axis
    // wrote one slider value to all three PeakVue baselines.
    if (!pvTargetAxisLabel || pvTargetAxisId == null) {
      setPvSaveError('Isolate a single axis (V, H or A) before saving — thresholds are set per axis.');
      return;
    }
    if (!(pvSlideSev > pvSlideMod)) {
      setPvSaveError('Critical threshold must be greater than the alarm threshold.');
      return;
    }
    setPvSaving(true);
    setPvSaveError(null);
    try {
      await apiFetch(`/alarm-manager/peakvue/${pvTargetAxisId}`, {
        method: 'PATCH',
        body: JSON.stringify({ moderate: pvSlideMod, severe: pvSlideSev }),
      });
      // Reflect the saved values on the target axis only
      setPvAxisThresholds(prev => ({
        ...prev,
        [pvTargetAxisLabel]: { moderate: pvSlideMod, severe: pvSlideSev, is_fallback: false },
      }));
      setPvDirty(false);
    } catch (err: any) {
      setPvSaveError(err?.message ?? 'Save failed — check you have admin access');
    } finally {
      setPvSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header — sticky while scrolling. Shared with the machine page. */}
      <HierarchyBreadcrumb
        hierarchy={hierarchy ?? null}
        machineName={machineName ?? ''}
        pointName={point.name}
        onBack={onBack}
        statusColor={getStatusColor(point.status)}
      />

      <div className="space-y-6">
          {/* Current Values */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="text-gray-600 mb-2">Velocity <span className="text-[10px] text-gray-400">(latest)</span></div>
              <div className="text-gray-900">V: {fmtCur(curVel?.V, 2)} in/s</div>
              <div className="text-gray-900">H: {fmtCur(curVel?.H, 2)} in/s</div>
              <div className="text-gray-900">A: {fmtCur(curVel?.A, 2)} in/s</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <div className="text-gray-600 mb-2">Acceleration <span className="text-[10px] text-gray-400">(latest)</span></div>
              <div className="text-gray-900">V: {fmtCur(curAccel?.V, 2)} G&apos;s</div>
              <div className="text-gray-900">H: {fmtCur(curAccel?.H, 2)} G&apos;s</div>
              <div className="text-gray-900">A: {fmtCur(curAccel?.A, 2)} G&apos;s</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="text-gray-600 mb-2">Envelope <span className="text-[10px] text-gray-400">(latest)</span></div>
              <div className="text-gray-900">V: {fmtCur(curEnv?.V, 2)} G&apos;s</div>
              <div className="text-gray-900">H: {fmtCur(curEnv?.H, 2)} G&apos;s</div>
              <div className="text-gray-900">A: {fmtCur(curEnv?.A, 2)} G&apos;s</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
              <div className="text-gray-600 mb-2">Temperature <span className="text-[10px] text-gray-400">(latest)</span></div>
              <div className="text-gray-900">{fmtCur(curTemp, 0)}&deg;F</div>
              <div className="text-[10px] text-gray-400 mt-1">sensor-level</div>
            </div>
          </div>

          {/* Primary trend card — mirrors machine page: tabs + shared time range */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 border border-gray-200 shadow-sm"
            style={cmp.compareMode ? { userSelect: 'none', WebkitUserSelect: 'none' } : undefined}>
            {/* Controls row */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                {([
                  { unit: 2,  label: 'Velocity' },
                  { unit: 0,  label: 'Acceleration' },
                  { unit: 6,  label: 'Envelope' },
                  ...(point.axisIdsByLabel && Object.keys(point.axisIdsByLabel).length > 0 ? [{ unit: -1 as number, label: 'Impact View' }] : []),
                ] as { unit: number; label: string }[]).map(({ unit, label }) => (
                  <button key={unit} onClick={() => setPrimaryUnit(unit)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${primaryUnit === unit ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >{label}</button>
                ))}
              </div>
              <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
                {([{d:7,l:'7D'},{d:30,l:'30D'},{d:90,l:'90D'},{d:180,l:'180D'},{d:365,l:'1Y'}] as {d:number;l:string}[]).map(({d,l},i) => (
                  <button key={d} onClick={() => setTrendDays(d)}
                    className={`px-2.5 py-1.5 ${i > 0 ? 'border-l border-gray-200' : ''} ${trendDays === d ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >{l}</button>
                ))}
              </div>
            </div>

            {signalFromUnit(primaryUnit) && (
              <div className="mb-2"><CompareControls cmp={cmp} /></div>
            )}
            {/* Chart body */}
            {primaryUnit === -1 ? (
              pvLoading ? (
                <div className="bg-gray-50 rounded animate-pulse" style={{ height: 300 }} />
              ) : Object.keys(pvAxisData).length === 0 ? (
                <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height: 300 }}>No Impact View data available for this point</div>
              ) : (() => {
                const allTimes = Array.from(new Set(Object.values(pvAxisData).flatMap(rows => rows.map(r => r.time)))).sort();
                const cd = allTimes.map(t => {
                  const row: any = { time: t };
                  for (const [lbl, rows] of Object.entries(pvAxisData)) {
                    const r = rows.find(x => x.time === t);
                    if (!r) continue;
                    row[lbl] = r.value;
                    row[`${lbl}__at`] = r.at;
                    if (r.ft) { row[`${lbl}__ft`] = r.ft; row[`${lbl}__pp`] = r.pp; row[`${lbl}__rel`] = r.rel; }
                  }
                  return row;
                });
                const axisLabels = Object.keys(pvAxisData);
                const visibleAxisLabels = axisLabels.filter(l => !pvHiddenAxes.has(l));
                return (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={cd} margin={{ top: 5, right: 24, left: 0, bottom: 5 }} style={{ cursor: 'pointer' }}
                        onMouseDown={cmp.compareMode ? cmp.onMouseDown : undefined}
                        onMouseUp={cmp.compareMode ? cmp.onMouseUp : undefined}
                        onMouseMove={(s: any) => {
                          if (cmp.compareMode) { cmp.onMouseMove(s); return; }
                          const ap = s?.activePayload;
                          if (s?.isTooltipActive && ap?.length > 0) {
                            const ak = ap.find((p: any) => !String(p.name).includes('__'))?.name;
                            const at = ak ? ap[0]?.payload?.[`${ak}__at`] : null;
                            if (at && ak) pvHoverRef.current = { t: at, axis: ak };
                          } else { pvHoverRef.current = null; }
                        }}
                        onMouseLeave={() => { pvHoverRef.current = null; }}
                        onClick={() => { if (cmp.compareMode) return; const h = pvHoverRef.current; if (h) setWaveformJumpTo({ measured_at: h.t, point_name: point.name, signal: 'peakvue', fromClick: true }); }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        {renderCompareAreas(cmp)}
                        <XAxis dataKey="time" stroke="#9CA3AF" tick={{ fontSize: 10 }} minTickGap={40} />
                        <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} label={{ value: 'G', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6b7280' } }} />
                        <Tooltip content={({ active, payload, label }: any) => {
                          if (!active || !payload?.length) return null;
                          const seen = new Set<string>();
                          const deduped = payload.filter((p: any) => { const k = String(p.dataKey); if (seen.has(k)) return false; seen.add(k); return true; });
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg p-2 text-xs shadow">
                              <div className="font-medium mb-1 text-gray-700">{label}</div>
                              {deduped.map((p: any, i: number) => {
                                const name = String(p.dataKey ?? '');
                                if (name.includes('__')) return null;
                                const ft = p.payload?.[`${name}__ft`]; const pp = p.payload?.[`${name}__pp`]; const rel = p.payload?.[`${name}__rel`];
                                const val = typeof p.value === 'number' ? p.value.toFixed(3) + ' G' : p.value;
                                const detail = ft ? `  |  ${PV_PLUS_FAULT_LABELS[ft] ?? ft}  (${pp != null ? Number(pp).toFixed(0) : '—'}% periodic)${rel === 'preliminary' ? '  ⚠' : ''}` : '';
                                return <div key={i} style={{ color: p.color }}>{name} : {val}{detail}</div>;
                              })}
                            </div>
                          );
                        }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {/* Per-axis moderate / severe alarm thresholds */}
                        {axisLabels.map(lbl => {
                          const t = pvAxisThresholds[lbl];
                          if (!t) return null;
                          const c = AXIS_COLORS[lbl] ?? '#6b7280';
                          const decimals = 2;
                          return (
                            <Fragment key={`thr-${lbl}`}>
                              {t.moderate > 0 && <ReferenceLine y={t.moderate} stroke={c} strokeWidth={1.5} strokeDasharray="6 3"
                                label={{ value: `${lbl} Mod: ${t.moderate.toFixed(decimals)}G`, position: 'insideTopRight', fill: c, fontSize: 9 }} />}
                              {t.severe   > 0 && <ReferenceLine y={t.severe}   stroke={c} strokeWidth={1}   strokeDasharray="3 2"
                                label={{ value: `${lbl} Sev: ${t.severe.toFixed(decimals)}G`,   position: 'insideBottomRight', fill: c, fontSize: 9 }} />}
                            </Fragment>
                          );
                        })}
                        {axisLabels.map((lbl) => !visibleAxisLabels.includes(lbl) ? null : (
                          <Fragment key={lbl}>
                            <Line type="monotone" dataKey={lbl} stroke={AXIS_COLORS[lbl] ?? '#2563eb'} strokeWidth={1.5} connectNulls strokeDasharray="4 4" strokeOpacity={0.4} legendType="none" isAnimationActive={false} />
                            <Line type="monotone" dataKey={lbl} stroke={AXIS_COLORS[lbl] ?? '#2563eb'} strokeWidth={2} connectNulls name={lbl} isAnimationActive={false}
                              dot={(props: any) => {
                                const { cx, cy, payload, index } = props;
                                if (cx == null || cy == null) return <></>;
                                const ft = payload[`${lbl}__ft`]; const rel = payload[`${lbl}__rel`]; const pp = payload[`${lbl}__pp`];
                                const ppNum = pp != null ? Number(pp) : 0;
                                const isHP = ft === 'highly_periodic' || ft === 'bearing_fault';
                                const isLast = index === cd.length - 1;
                                if (isHP && ppNum >= 75) return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill={PV_PLUS_FAULT_COLOURS['highly_periodic']} stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                                if (isHP && ppNum >= 50) return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill="#eab308" stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                                if (ft && !isHP) return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill={PV_PLUS_FAULT_COLOURS[ft] ?? '#6b7280'} stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                                if (isLast) return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={4} fill={AXIS_COLORS[lbl] ?? '#2563eb'} stroke="white" strokeWidth={1.5} />;
                                return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={2} fill={AXIS_COLORS[lbl] ?? '#2563eb'} fillOpacity={0.5} />;
                              }}
                            />
                          </Fragment>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-1.5 pt-1 pb-0.5">
                      {axisLabels.map(lbl => {
                        const color = AXIS_COLORS[lbl] ?? '#2563eb';
                        const isHidden = pvHiddenAxes.has(lbl);
                        return (
                          <button key={lbl} onClick={() => togglePvAxis(lbl)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
                            style={{ borderColor: color, background: isHidden ? '#f8fafc' : 'white', opacity: isHidden ? 0.45 : 1 }}
                          >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isHidden ? 'transparent' : color, border: `1.5px solid ${color}` }} />
                            <span style={{ color: isHidden ? '#9ca3af' : color }}>{lbl}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                      <span className="text-gray-400">Plus dots (≥5G):</span>
                      {[['hp','#dc2626','Highly Periodic (≥75%)'],['hp2','#eab308','Moderately Periodic (50–74%)'],['he','#16a34a','Non-periodic']].map(([k,col,lbl]) => (
                        <span key={k} className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: col }} />{lbl}</span>
                      ))}
                    </div>

                    {/* PeakVue alarm threshold controls — mirrors vel/accel section */}
                    <div className="mt-4 space-y-3 border-t border-gray-100 pt-3">
                      {Object.keys(pvAxisThresholds).length > 0 && (
                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                          <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">Per-Axis Thresholds (G)</div>
                          <div className="grid grid-cols-3 gap-1">
                            {(['V', 'H', 'A'] as const).map(a => {
                              const t = pvAxisThresholds[a];
                              const active = pvTargetAxisLabel === a;
                              return (
                                <button key={a} type="button"
                                  onClick={() => setPvHiddenAxes(new Set(['V', 'H', 'A'].filter(x => x !== a)))}
                                  title={`Edit the ${a} axis threshold`}
                                  className="flex flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-white"
                                  style={{ opacity: pvHiddenAxes.has(a) ? 0.3 : 1, background: active ? `${AXIS_COLORS[a]}18` : 'transparent', border: active ? `1px solid ${AXIS_COLORS[a]}40` : '1px solid transparent' }}>
                                  <div className="flex items-center gap-1 mb-0.5">
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: AXIS_COLORS[a] }} />
                                    <span className="text-xs font-semibold text-gray-700">{a}</span>
                                    {t?.is_fallback && <span className="text-[9px] text-gray-400 ml-0.5">Emerson</span>}
                                  </div>
                                  {t ? (
                                    <>
                                      <span className="text-xs tabular-nums" style={{ color: '#b45309' }}>3σ: {t.moderate.toFixed(2)}</span>
                                      <span className="text-xs tabular-nums text-red-600">Sev: {t.severe.toFixed(2)}</span>
                                    </>
                                  ) : <span className="text-xs text-gray-400">—</span>}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-[11px] text-gray-400 mt-1.5">
                            {pvTargetAxisLabel
                              ? `Editing ${pvTargetAxisLabel} — saves apply to this axis only.`
                              : 'Select an axis above to edit its threshold.'}
                          </p>
                        </div>
                      )}
                      {(() => {
                        const pvAxisLabel = pvTargetAxisLabel ? `${pvTargetAxisLabel}-Axis ` : '';
                        // Same anchoring as the RMS sliders — range from the persisted
                        // threshold, never from the live value (see the note there).
                        const pvAnchor = (pvTargetAxisLabel ? pvAxisThresholds[pvTargetAxisLabel]?.severe : undefined)
                                         ?? Math.max(0, ...Object.values(pvAxisThresholds).map(t => t?.severe ?? 0));
                        const pvBase = Math.max(pvAnchor, 10) * 1.5;
                        const pvNeed = Math.max(pvSlideMod, pvSlideSev, 0);
                        const pvMax  = pvBase * Math.pow(2, pvNeed > pvBase ? Math.min(Math.ceil(Math.log2(pvNeed / pvBase)), 10) : 0);
                        const pvStep = niceStep(pvMax);
                        const pvInvalid = !(pvSlideSev > pvSlideMod);
                        return (
                          <>
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-600">{pvAxisLabel}Alarm Threshold (3σ)</span>
                                <ThresholdInput
                                  value={pvSlideMod} decimals={2} step={pvStep} unitLabel="G"
                                  label={`${pvAxisLabel}PeakVue alarm threshold in G`}
                                  disabled={!pvTargetAxisLabel}
                                  onCommit={v => handlePvChange('moderate', v)}
                                />
                              </div>
                              <input type="range" min="0" max={pvMax} step={pvStep}
                                disabled={!pvTargetAxisLabel}
                                value={Math.min(pvSlideMod, pvMax)}
                                onChange={(e) => handlePvChange('moderate', Number(e.target.value))}
                                className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider disabled:opacity-40 disabled:cursor-not-allowed" />
                            </div>
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-600">{pvAxisLabel}Critical Threshold (3σ ×1.5)</span>
                                <ThresholdInput
                                  value={pvSlideSev} decimals={2} step={pvStep} unitLabel="G"
                                  label={`${pvAxisLabel}PeakVue critical threshold in G`}
                                  disabled={!pvTargetAxisLabel}
                                  onCommit={v => handlePvChange('severe', v)}
                                />
                              </div>
                              <input type="range" min="0" max={pvMax} step={pvStep}
                                disabled={!pvTargetAxisLabel}
                                value={Math.min(pvSlideSev, pvMax)}
                                onChange={(e) => handlePvChange('severe', Number(e.target.value))}
                                className="w-full h-2 bg-red-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider disabled:opacity-40 disabled:cursor-not-allowed" />
                            </div>
                            {pvDirty && (
                              <>
                                <button onClick={handlePvSave}
                                  disabled={pvSaving || !pvTargetAxisLabel || pvInvalid}
                                  className="w-full py-2 rounded-lg text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 disabled:hover:bg-blue-600">
                                  {pvSaving ? 'Saving…' : pvTargetAxisLabel ? `Save ${pvTargetAxisLabel}-Axis Thresholds` : 'Save Thresholds'}
                                </button>
                                {!pvTargetAxisLabel && (
                                  <p className="text-xs text-amber-600 mt-1 font-medium">
                                    Thresholds are set per axis — select a single axis (V, H or A) to save.
                                  </p>
                                )}
                                {pvInvalid && (
                                  <p className="text-xs text-red-500 mt-1 font-medium">
                                    Critical must be greater than the alarm threshold.
                                  </p>
                                )}
                                {pvSaveError && !pvSaving && <p className="text-xs text-red-500 mt-1">{pvSaveError}</p>}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </>
                );
              })()
            ) : (() => {
              // Standard chart (Velocity / Acceleration / Envelope)
              const uc = primaryUnit;
              const filtered    = uc === 2 ? filteredVel   : uc === 0 ? filteredAccel : filteredEnv;
              // F-25: no synthetic fill — genuinely-empty trend renders an honest empty state.
              if (!filtered || filtered.length === 0) {
                return <div className="py-6"><NoData message="No trend data for the selected period" /></div>;
              }
              const thrByAxis   = uc === 2 ? velThrByAxis  : uc === 0 ? accelThrByAxis : envThrByAxis;
              const thresholds  = uc === 2 ? velocityThresholds : uc === 0 ? accelerationThresholds : envelopeThresholds;
              const metricKey   = uc === 2 ? 'velocity'    : uc === 0 ? 'acceleration' : 'envelope';
              const dirty       = uc === 2 ? velocityDirty : uc === 0 ? accelerationDirty : envelopeDirty;
              const unitLabel   = uc === 2 ? 'in/s'        : uc === 0 ? 'G' : 'GE';
              const decimals    = uc === 2 ? 3             : 4;
              const floor       = uc === 2 ? velFloor      : uc === 0 ? accelFloor : envFloor;
              const setFloor    = uc === 2 ? setVelFloor   : uc === 0 ? setAccelFloor : setEnvFloor;
              const wfMarkers   = wfMarkersMap[uc];
              const hasByAxis   = Object.keys(thrByAxis).length > 0;
              const visibleAxes = (['V', 'H', 'A'] as const).filter(a => !hiddenAxes.has(a));
              const visAxes     = visibleAxes;
              const axisLabel   = targetAxisLabel ? `${targetAxisLabel}-Axis ` : '';
              const maxYellow   = hasByAxis ? Math.max(0, ...visibleAxes.map(a => thrByAxis[a]?.yellow ?? 0)) : thresholds.yellow;
              // Override state belongs to the axis being edited, not the point.
              const overrideMeta = overrideFor(metricKey, targetAxisLabel);
              const yMax        = calcYMax(filtered, maxYellow);
              // Slider track. The range is anchored to the PERSISTED threshold of the
              // axis being edited — never to the live value the slider controls. The
              // old `max={thresholds.red * 2}` did the latter, and because dragging
              // moderate also recomputes critical (×1.5), the ceiling grew in lockstep
              // with the value: the thumb stayed pinned at a third of the track while
              // the number ran away under the cursor. That runaway is the "too
              // sensitive" behaviour. Anchored to props, the track is stable for the
              // whole drag; it widens only in discrete doublings, so a typed value
              // outside the track can never make the range chase it continuously.
              const anchorRed   = (targetAxisLabel ? thrByAxis[targetAxisLabel]?.red : undefined)
                                  ?? (hasByAxis ? Math.max(0, ...visibleAxes.map(a => thrByAxis[a]?.red ?? 0)) : 0);
              const baseMax     = Math.max(anchorRed, uc === 2 ? 0.1 : 0.5) * 1.5;
              const need        = Math.max(thresholds.yellow, thresholds.red, 0);
              const doublings   = need > baseMax ? Math.min(Math.ceil(Math.log2(need / baseMax)), 10) : 0;
              const sliderMax   = baseMax * Math.pow(2, doublings);
              const step        = niceStep(sliderMax);
              const invalidPair = !(thresholds.red > thresholds.yellow);
              return (
                <>
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={floor} setFloor={setFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0" style={{ cursor: wfHover?.unitCode === uc ? 'pointer' : 'default' }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={filtered} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          onMouseDown={cmp.compareMode ? cmp.onMouseDown : undefined}
                          onMouseUp={cmp.compareMode ? cmp.onMouseUp : undefined}
                          onMouseMove={(s: any) => {
                            if (cmp.compareMode) { cmp.onMouseMove(s); return; }
                            if (s.isTooltipActive && s.activeTooltipIndex !== undefined) {
                              const nw = findNearestWfPt(filtered, s.activeTooltipIndex, wfMarkers);
                              setWfHover(nw ? { unitCode: uc, ...nw } : null);
                            } else { setWfHover(null); }
                          }}
                          onMouseLeave={() => setWfHover(null)}
                          onClick={() => { if (cmp.compareMode) return; const h = wfHoverRef.current; if (h?.unitCode === uc) setWaveformJumpTo({ measured_at: h.measured_at, point_name: point.name, fromClick: true }); }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          {renderCompareAreas(cmp)}
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, yMax]} tickFormatter={v => v.toFixed(decimals)} />
                          <Tooltip formatter={(v: number) => [typeof v === 'number' ? v.toFixed(decimals) : v, '']} />
                          {hasByAxis
                            ? visibleAxes.map(a => {
                                const t = thrByAxis[a]; if (!t) return null;
                                const c = AXIS_COLORS[a]; const single = visibleAxes.length === 1;
                                return (
                                  <Fragment key={a}>
                                    {t.yellow > 0 && <ReferenceLine y={t.yellow} stroke={c} strokeWidth={2} strokeDasharray="6 3" label={{ value: single ? `Mod: ${t.yellow.toFixed(decimals)}` : `${a} ${t.yellow.toFixed(decimals)}`, position: 'insideTopRight', fill: c, fontSize: 10 }} />}
                                    {t.red    > 0 && <ReferenceLine y={t.red}    stroke={c} strokeWidth={1.5} strokeDasharray="3 2" label={{ value: single ? `Sev: ${t.red.toFixed(decimals)}`    : `${a} ${t.red.toFixed(decimals)}`,    position: 'insideBottomRight', fill: c, fontSize: 10 }} />}
                                  </Fragment>
                                );
                              })
                            : <>
                                {thresholds.yellow > 0 && <ReferenceLine y={thresholds.yellow} stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Mod: ${thresholds.yellow.toFixed(decimals)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10 }} />}
                                {thresholds.red    > 0 && <ReferenceLine y={thresholds.red}    stroke="#EF4444" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Sev: ${thresholds.red.toFixed(decimals)}`,    position: 'insideTopRight', fill: '#ef4444', fontSize: 10 }} />}
                              </>
                          }
                          {floor > 0 && <ReferenceLine y={floor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${floor.toFixed(3)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          {wfHover?.unitCode === uc && <ReferenceLine x={wfHover.time} stroke={wfMarkerColour(wfHover.wf_state)} strokeWidth={2} strokeDasharray={wfMarkerDash(wfHover.wf_state)} label={{ value: fmtWfLabel(wfHover.measured_at, wfHover.wf_state), position: 'insideBottomLeft', fill: wfMarkerColour(wfHover.wf_state), fontSize: 9, fontWeight: 600 }} />}
                          <Line type="monotone" dataKey="V" stroke={AXIS_COLORS.V} isAnimationActive={false} connectNulls dot={makeDot(uc, AXIS_COLORS.V, filtered.length - 1)} hide={hiddenAxes.has('V')} />
                          <Line type="monotone" dataKey="H" stroke={AXIS_COLORS.H} isAnimationActive={false} connectNulls dot={makeDot(uc, AXIS_COLORS.H, filtered.length - 1)} hide={hiddenAxes.has('H')} />
                          <Line type="monotone" dataKey="A" stroke={AXIS_COLORS.A} isAnimationActive={false} connectNulls dot={makeDot(uc, AXIS_COLORS.A, filtered.length - 1)} hide={hiddenAxes.has('A')} />
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-2 pt-1">
                        {(['V', 'H', 'A'] as const).map(axis => (
                          <button key={axis} onClick={() => toggleAxis(axis)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
                            style={{ borderColor: AXIS_COLORS[axis], background: 'white', opacity: hiddenAxes.has(axis) ? 0.4 : 1 }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: AXIS_COLORS[axis] }} />{axis}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Alarm threshold controls */}
                  <div className="mt-4 space-y-3 border-t border-gray-100 pt-3">
                    {Object.keys(thrByAxis).length > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                        <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">Per-Axis Thresholds ({unitLabel})</div>
                        <div className="grid grid-cols-3 gap-1">
                          {(['V', 'H', 'A'] as const).map(a => {
                            const t = thrByAxis[a];
                            const active = targetAxisLabel === a;
                            const ov = overrideFor(metricKey, a);
                            return (
                              <button key={a} type="button" onClick={() => isolateAxis(a)}
                                title={`Edit the ${a} axis threshold`}
                                className="flex flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-white"
                                style={{ opacity: hiddenAxes.has(a) ? 0.3 : 1, background: active ? `${AXIS_COLORS[a]}18` : 'transparent', border: active ? `1px solid ${AXIS_COLORS[a]}40` : '1px solid transparent' }}>
                                <div className="flex items-center gap-1 mb-0.5">
                                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: AXIS_COLORS[a] }} />
                                  <span className="text-xs font-semibold text-gray-700">{a}</span>
                                  {ov.active && <span className="text-[9px] text-amber-600 ml-0.5">manual</span>}
                                </div>
                                {t ? (
                                  <>
                                    <span className="text-xs tabular-nums" style={{ color: '#b45309' }}>3σ: {t.yellow.toFixed(decimals)}</span>
                                    <span className="text-xs tabular-nums text-red-600">Sev: {t.red.toFixed(decimals)}</span>
                                  </>
                                ) : <span className="text-xs text-gray-400">—</span>}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1.5">
                          {targetAxisLabel
                            ? `Editing ${targetAxisLabel} — saves apply to this axis only.`
                            : 'Select an axis above to edit its threshold.'}
                        </p>
                      </div>
                    )}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">{axisLabel}Alarm Threshold (3σ)</span>
                          {overrideMeta.active && !dirty && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                              title={overrideMeta.setAt ? `Manual override set ${new Date(overrideMeta.setAt).toLocaleString()}${overrideMeta.setBy ? ` by ${overrideMeta.setBy}` : ''}` : 'Manual override active'}
                            >
                              manual
                            </span>
                          )}
                        </div>
                        <ThresholdInput
                          value={thresholds.yellow} decimals={decimals} step={step} unitLabel={unitLabel}
                          label={`${axisLabel}alarm threshold in ${unitLabel}`}
                          disabled={!targetAxisLabel}
                          onCommit={v => handleThresholdChange(metricKey, 'yellow', v)}
                        />
                      </div>
                      {/* Locked until an axis is chosen: switching axes discards a
                          pending edit, so letting one be made first only loses work. */}
                      <input type="range" min="0" max={sliderMax} step={step}
                        disabled={!targetAxisLabel}
                        value={Math.min(thresholds.yellow, sliderMax)}
                        onChange={(e) => handleThresholdChange(metricKey, 'yellow', Number(e.target.value))}
                        className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider disabled:opacity-40 disabled:cursor-not-allowed" />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">{axisLabel}Critical Threshold (3σ ×1.5)</span>
                        <ThresholdInput
                          value={thresholds.red} decimals={decimals} step={step} unitLabel={unitLabel}
                          label={`${axisLabel}critical threshold in ${unitLabel}`}
                          disabled={!targetAxisLabel}
                          onCommit={v => handleThresholdChange(metricKey, 'red', v)}
                        />
                      </div>
                      <input type="range" min="0" max={sliderMax} step={step}
                        disabled={!targetAxisLabel}
                        value={Math.min(thresholds.red, sliderMax)}
                        onChange={(e) => handleThresholdChange(metricKey, 'red', Number(e.target.value))}
                        className="w-full h-2 bg-red-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider disabled:opacity-40 disabled:cursor-not-allowed" />
                    </div>
                    {dirty ? (
                      <div className="space-y-1.5">
                        <button onClick={() => handleSave(metricKey)}
                          disabled={savingMetric === metricKey || !targetAxisLabel || invalidPair}
                          className="w-full py-2 rounded-lg text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 disabled:hover:bg-blue-600">
                          {savingMetric === metricKey ? 'Saving…' : targetAxisLabel ? `Save ${targetAxisLabel}-Axis Thresholds` : 'Save Thresholds'}
                        </button>
                        {!targetAxisLabel && (
                          <p className="text-xs text-amber-600 font-medium">
                            Thresholds are set per axis — select a single axis (V, H or A) to save.
                          </p>
                        )}
                        {invalidPair && (
                          <p className="text-xs text-red-500 font-medium">
                            Critical must be greater than the alarm threshold.
                          </p>
                        )}
                        {saveError && savingMetric === null && (
                          <p className="text-xs text-red-500 font-medium">{saveError}</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {savedMetric === metricKey && (
                          <p className="text-xs text-green-600 font-medium">
                            Saved {axisLabel}— {thresholds.yellow.toFixed(decimals)} {unitLabel}
                          </p>
                        )}
                        {savedMetric === metricKey + '_cleared' && (
                          <p className="text-xs text-green-600 font-medium">
                            {axisLabel}override cleared — reverted to baseline
                          </p>
                        )}
                        {/* Shown only with a single axis selected: overrideFor() reports
                            inactive when there is no unambiguous target, so this cannot
                            offer to clear an axis the operator has not chosen. */}
                        {overrideMeta.active && targetAxisLabel && (
                          <button
                            onClick={() => handleClearOverride(metricKey)}
                            disabled={savingMetric === metricKey}
                            className="w-full py-1.5 rounded-lg text-xs font-medium transition-colors border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
                          >
                            {savingMetric === metricKey ? 'Clearing…' : `Reset ${targetAxisLabel} to baseline`}
                          </button>
                        )}
                        {saveError && savingMetric === null && (
                          <p className="text-xs text-red-500 font-medium">{saveError}</p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {cmp.compareMode && cmp.windowA && cmp.windowB && signalFromUnit(primaryUnit) && (() => {
            const ids = point.axisIdsByLabel;
            const hidden = primaryUnit === -1 ? pvHiddenAxes : hiddenAxes;
            const order = ['V', 'H', 'A'];
            const pick = order.find(l => !hidden.has(l) && ids?.[l] != null) ?? order.find(l => ids?.[l] != null);
            const axisId = pick && ids ? ids[pick] : undefined;
            return axisId ? (
              <TrendComparePanel level="point" signal={signalFromUnit(primaryUnit)!} trendDays={trendDays}
                windowA={cmp.windowA!} windowB={cmp.windowB!} axisId={axisId} />
            ) : null;
          })()}

          {/* Temperature chart — separate small card */}
          {filteredTemp.length > 0 && (
            <ChartContainer title="Temperature (°F)">
              {(() => {
                const yMax = calcYMax(filteredTemp);
                return (
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={tempFloor} setFloor={setTempFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0">
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={filteredTemp} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis unit="°F" domain={[0, yMax]} />
                          <Tooltip formatter={(v: number) => [`${v}°F`, 'Sensor Temp']} />
                          {tempFloor > 0 && <ReferenceLine y={tempFloor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${tempFloor.toFixed(1)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          <Line type="monotone" dataKey="temp" stroke="#F97316" strokeWidth={2} isAnimationActive={false} connectNulls name="Sensor Temp"
                            dot={(p: any) => { const { cx, cy, index } = p; if (!cx || !cy) return <circle key={index} r={0} />; const isLast = index === filteredTemp.length - 1; return <circle key={index} cx={cx} cy={cy} r={isLast ? 4 : 2} fill="#F97316" stroke={isLast ? 'white' : 'none'} strokeWidth={isLast ? 1.5 : 0} fillOpacity={isLast ? 1 : 0.6} />; }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
            </ChartContainer>
          )}

          {/* General-purpose channels — one card per channel, alongside H/V/A.
              Renders only where a GP sensor is actually configured, so a vibration
              point is visually identical to before. Each channel gets its own card
              because each is a different variable in its own engineering unit. */}
          {gpSeries.map(s => (
            <ChartContainer
              key={s.axisId}
              title={s.unit ? `${s.label} (${s.unit})` : s.label}
            >
              {(() => {
                const yMax = calcYMax(s.rows);   // calcYMax ignores the 'time' key
                return (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={s.rows} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                      <YAxis unit={s.unit ? ` ${s.unit}` : undefined} domain={[0, yMax]} />
                      <Tooltip formatter={(v: number) => [`${v}${s.unit ? ` ${s.unit}` : ''}`, s.label]} />
                      <Line
                        type="monotone" dataKey="value" stroke="#0EA5E9" strokeWidth={2}
                        isAnimationActive={false} connectNulls name={s.label}
                        dot={(p: any) => {
                          const { cx, cy, index } = p;
                          if (!cx || !cy) return <circle key={index} r={0} />;
                          const isLast = index === s.rows.length - 1;
                          return (
                            <circle
                              key={index} cx={cx} cy={cy} r={isLast ? 4 : 2} fill="#0EA5E9"
                              stroke={isLast ? 'white' : 'none'} strokeWidth={isLast ? 1.5 : 0}
                              fillOpacity={isLast ? 1 : 0.6}
                            />
                          );
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                );
              })()}
            </ChartContainer>
          ))}

          {/* Waveform Viewer */}
          {point.machineId && (
            <WaveformViewer
              machineId={point.machineId}
              jumpTo={waveformJumpTo}
              operatingRpm={null}
              isVfd={false}
              compact={compareViewer}
              onAddCompare={compareViewer ? undefined : () => setCompareViewer(true)}
            />
          )}

          {/* Comparison viewer — no jumpTo, so chart clicks drive viewer 1 only. */}
          {point.machineId && compareViewer && (
            <WaveformViewer
              machineId={point.machineId}
              operatingRpm={null}
              isVfd={false}
              compact
              summaryCollapsed
              onRemove={() => setCompareViewer(false)}
            />
          )}
      </div>
    </div>
  );
}
